import { useState } from 'react';
import { Group, Text, ActionIcon, Badge, Popover, Stack, Divider, ScrollArea } from '@mantine/core';
import { Bell, SignOut, Gear } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../store/auth.store';
import { useUIStore } from '../../store/ui.store';
import { formatRelative } from '../../utils/formatters';
import styles from './Header.module.css';
import { useAdminNotifications, useAdminMarkNotificationRead } from '../../hooks/admin/useAdminNotifications';
import { ProfileSettingsModal } from '../profile/ProfileSettingsModal';
import { adminApi, developerApi } from '../../api';
import { ThemeToggle } from './ThemeToggle';

interface HeaderProps {
  title?: string;
  breadcrumbs?: { label: string; path?: string }[];
}

export function Header({ title, breadcrumbs }: HeaderProps) {
  const { user, logout, refreshToken } = useAuthStore();
  const { sidebarCollapsed } = useUIStore();
  const navigate = useNavigate();

  const isAdmin = user?.role === 'platform_admin' || user?.role === 'admin';

  // Only call admin notifications if user is platform admin
  const { data: realNotifications } = useAdminNotifications({ enabled: isAdmin });
  const markRead = useAdminMarkNotificationRead();

  const [settingsModalOpened, setSettingsModalOpened] = useState(false);
  const [activeSettingsTab, setActiveSettingsTab] = useState('profile');

  const notifications = isAdmin && Array.isArray(realNotifications)
    ? realNotifications.map((n) => ({
        id: n.id,
        title: n.type,
        body: n.message,
        is_read: !!n.readAt,
        created_at: n.createdAt,
      }))
    : [];

  const unread = notifications.filter((n) => !n.is_read).length;

  const handleLogout = async () => {
    try {
      if (isAdmin) {
        if (refreshToken) {
          await adminApi.post('/v1/admin/auth/logout', { refreshToken }).catch(() => {});
        }
      } else {
        await developerApi.post('/v1/auth/logout').catch(() => {});
      }
    } finally {
      logout();
      navigate('/login');
    }
  };

  const openSettings = (tab: string) => {
    setActiveSettingsTab(tab);
    setSettingsModalOpened(true);
  };

  return (
    <header
      className={styles.header}
      style={{ left: sidebarCollapsed ? 56 : 220 }}
    >
      {/* ── Left: breadcrumbs / title ─────────────────────────────────────── */}
      <div className={styles.left}>
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <Group gap={6}>
            {breadcrumbs.map((crumb, i) => (
              <Group key={i} gap={6}>
                {i > 0 && <Text c="var(--text-disabled)" size="sm">/</Text>}
                <Text
                  size="sm"
                  c={i === breadcrumbs.length - 1 ? 'var(--text-primary)' : 'var(--text-secondary)'}
                  fw={i === breadcrumbs.length - 1 ? 500 : 400}
                  style={{ cursor: crumb.path ? 'pointer' : 'default' }}
                  onClick={() => crumb.path && navigate(crumb.path)}
                >
                  {crumb.label}
                </Text>
              </Group>
            ))}
          </Group>
        ) : title ? (
          <Text size="md" fw={600} c="var(--text-primary)">{title}</Text>
        ) : null}
      </div>

      {/* ── Right: theme toggle + notifications + profile menu ────────────── */}
      <Group gap={12}>
        {/* Role Badge */}
        <Badge
          size="sm"
          variant="filled"
          style={{
            background: isAdmin ? 'var(--amber)' : 'var(--green)',
            color: '#FFFFFF',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
          }}
        >
          {isAdmin ? 'Admin' : 'Developer'}
        </Badge>

        {/* Theme Switcher Toggle */}
        <ThemeToggle />

        {/* Notifications (Admin Only) */}
        {isAdmin && (
          <Popover width={320} position="bottom-end" shadow="md">
            <Popover.Target>
              <div className={styles.notifBtn}>
                <ActionIcon variant="subtle" size="md" color="gray">
                  <Bell size={18} weight="duotone" />
                </ActionIcon>
                {unread > 0 && (
                  <span className={styles.badge}>{unread}</span>
                )}
              </div>
            </Popover.Target>
            <Popover.Dropdown style={{ background: 'var(--surface)', border: '1px solid var(--boundary)', padding: 0 }}>
              <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--boundary)' }}>
                <Group justify="space-between">
                  <Text size="xs" fw={600} c="var(--text-primary)" style={{ textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                    Notifications
                  </Text>
                  {unread > 0 && (
                    <Text size="xs" c="var(--amber)" style={{ cursor: 'pointer', fontWeight: 600 }}>
                      {unread} new
                    </Text>
                  )}
                </Group>
              </div>
              <ScrollArea.Autosize mah={280}>
                {notifications.length === 0 ? (
                  <Text size="xs" c="var(--text-secondary)" style={{ padding: '20px 14px', textAlign: 'center' }}>
                    No notifications
                  </Text>
                ) : (
                  notifications.map((notif) => (
                    <div
                      key={notif.id}
                      onClick={() => !notif.is_read && markRead.mutate(notif.id)}
                      style={{
                        padding: '10px 14px',
                        borderBottom: '1px solid var(--boundary-subtle)',
                        background: notif.is_read ? 'transparent' : 'var(--amber-dim)',
                        cursor: 'pointer',
                      }}
                    >
                      <Group justify="space-between" mb={2}>
                        <Text size="xs" fw={600} c={notif.is_read ? 'var(--text-secondary)' : 'var(--text-primary)'}>
                          {notif.title}
                        </Text>
                        <Text size="xs" c="var(--text-disabled)" style={{ fontFamily: 'Geist Mono, monospace', fontSize: 10 }}>
                          {formatRelative(notif.created_at)}
                        </Text>
                      </Group>
                      <Text size="xs" c="var(--text-secondary)" lineClamp={2}>
                        {notif.body}
                      </Text>
                    </div>
                  ))
                )}
              </ScrollArea.Autosize>
            </Popover.Dropdown>
          </Popover>
        )}

        {/* User profile dropdown */}
        <Popover width={220} position="bottom-end" shadow="md">
          <Popover.Target>
            <button className={styles.userBtn}>
              <div className={styles.avatar}>
                {user?.full_name ? user.full_name[0].toUpperCase() : 'U'}
              </div>
              <span className={styles.userName}>{user?.full_name ?? user?.email ?? 'User'}</span>
            </button>
          </Popover.Target>
          <Popover.Dropdown style={{ background: 'var(--surface)', border: '1px solid var(--boundary)', padding: 8 }}>
            <Stack gap={4}>
              <div style={{ padding: '6px 8px' }}>
                <Text size="xs" fw={600} c="var(--text-primary)" truncate>{user?.full_name}</Text>
                <Text size="xs" c="var(--text-secondary)" truncate>{user?.email}</Text>
              </div>
              <Divider style={{ borderColor: 'var(--boundary)' }} />

              <button
                className={styles.menuItem}
                onClick={() => openSettings(isAdmin ? 'appearance' : 'appearance')}
              >
                <Gear size={14} />
                <span>Settings & Appearance</span>
              </button>

              <Divider style={{ borderColor: 'var(--boundary)' }} />

              <button
                className={`${styles.menuItem} ${styles.menuItemDanger}`}
                onClick={handleLogout}
              >
                <SignOut size={14} />
                <span>Sign Out</span>
              </button>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Group>

      {/* Settings Modal */}
      <ProfileSettingsModal
        opened={settingsModalOpened}
        onClose={() => setSettingsModalOpened(false)}
        initialTab={activeSettingsTab}
      />
    </header>
  );
}
